import { Card, Col, Form, Row, Select, Typography, theme } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import DeviceInformation from "../../components/device/DeviceInformation";
import registerNode from "../../functions/single_line_diagram/registerNode";
import { Graph, Shape } from "@antv/x6";
import useFetch from "../../hooks/useFetch";
import { IndexContext } from "../../context/IndexContext";
import { genRadiusStyle } from "antd/es/input-number/style";

const { Text, Title } = Typography;
const { useToken } = theme;

function SingleLineDiagramPage() {
  const { authenticationState, siteSelected, siteList } =
    useContext(IndexContext);
  const graphRef = useRef(null);
  const { token } = useToken();
  const [fetchWrapper] = useFetch();
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState({ title: "-" });
  const [sldList, setSldList] = useState(null);
  const [selectedSld, setSelectedSld] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form] = Form.useForm();

  function getSldList() {
    if (siteSelected == null) return;

    fetchWrapper({
      endpoint_url: "single_line_diagram/getSingleLineDiagramList",
      method: "GET",
      params: { SiteID: siteSelected },
      onSuccess: (value) => {
        setSldList(value.data);
        if (selectedSld == null && value.data) {
          const id = value.data[0]?.["ID"] ?? null;
          setSelectedSld(id);
          form.setFieldValue("SldID", id);
        }
        setLoading(false);
      },
      onResponseError: (error) => {
        setSldList(false);
      },
    });
  }

  function getSld() {
    fetchWrapper({
      endpoint_url: "single_line_diagram/getSingleLineDiagramByID",
      method: "GET",
      params: { ID: selectedSld },
      onSuccess: (value) => {
        graphRef.current?.fromJSON(value.data["Cells"]);
        graphRef.current?.zoomToFit({
          padding: { left: 10, top: 10, right: 10, bottom: 10 },
          maxScale: 1,
        });
        getDPMData();
      },
      onResponseError: (error) => {
        setSldList(false);
      },
    });
  }

  function getDPMData() {
    if (siteSelected == null) return;

    fetchWrapper({
      endpoint_url: "single_line_diagram/getDPMData",
      method: "GET",
      params: { SiteID: siteSelected },
      onSuccess: (value) => {
        const dpms = value.data;
        dpms?.forEach((dpm) => {
          const node = graphRef.current?.getCellById(dpm["meter_key"]);

          node?.setData(dpm["data"], { overwrite: true });
          node?.setProp({
            label: dpm["meter_name"],
            meter_id: dpm["meter_id"],
            status: dpm["meter_status"],
          });
        });
      },
      onResponseError: (error) => {
        setSldList(false);
      },
    });
  }

  useEffect(() => {
    getSldList();
    registerNode();

    const graphContainer = document.getElementById("power-flow-diagram");
    const graph = new Graph({
      container: graphContainer,
      autoResize: true,
      connecting: {
        router: {
          name: "manhattan",
          args: {},
        },
        connector: {
          name: "rounded",
          args: {
            radius: 3,
          },
        },
        anchor: "center",
        connectionPoint: "anchor",
        allowBlank: false,
        snap: {
          radius: 1,
        },
        createEdge() {
          return new Shape.Edge({
            shape: "edge",
            attrs: {
              line: {
                stroke: "var(--ant-color-text-base)",
                strokeDasharray: 0,
                style: {
                  animation: "ant-line 30s infinite linear",
                },
                strokeWidth: 0.5,
                sourceMarker: false,
                targetMarker: false,
              },
            },
            zIndex: 0,
          });
        },
        validateConnection({ targetMagnet }) {
          return !!targetMagnet;
        },
      },
      panning: { enabled: true, modifiers: [], eventTypes: ["leftMouseDown"] },
      mousewheel: {
        enabled: true,
        zoomAtMousePosition: true,
        minScale: 0.3,
        maxScale: 1.5,
      },
      interacting: false,
    });

    graphRef.current = graph;

    graphRef.current?.on("node:click", ({ e, node }) => {
      if (["power-meter-simple", "power-meter-detail"].includes(node.shape)) {
        setSelectedDeviceId(node.getProp("meter_id" ?? 1));
        setOpenModal(true);
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      graphRef.current?.resize(
        graphContainer.parentElement.offsetWidth - 10,
        graphContainer.parentElement.offsetHeight
      );
      graphRef.current?.zoomToFit({
        padding: { left: 40, top: 20, right: 40, bottom: 20 },
        maxScale: 1,
      });
    });

    resizeObserver.observe(graphContainer.parentElement);

    const interval = setInterval(getDPMData, 30000);

    return () => {
      resizeObserver.disconnect();
      graphRef.current?.dispose();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    getSldList();
  }, [siteSelected]);

  useEffect(() => {
    if (selectedSld == null) return;
    getSld();
  }, [selectedSld]);

  return (
    <>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={8}>
          <Form form={form}>
            <Form.Item name={"SldID"} label="Single Line Diagram">
              <Select
                loading={loading}
                value={selectedSld}
                onChange={setSelectedSld}
              >
                {sldList?.map((item) => {
                  return (
                    <Select.Option key={item["ID"]} value={item["ID"]}>
                      {item["Name"]}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Form>
        </Col>
      </Row>
      <Row>
        <Col
          span={24}
          className="w-100"
          style={{ minHeight: "calc(100vh - 236px)", overflow: "hidden" }}
        >
          <div id="power-flow-diagram" className="w-100 h-100"></div>
        </Col>
      </Row>
      <DeviceInformation
        setOpenModal={setOpenModal}
        deviceID={selectedDeviceId}
        modalData={modalData}
        openModal={openModal}
        themeToken={token}
      />
    </>
  );
}

export default SingleLineDiagramPage;
